import React, { useState, useEffect } from "react";
// DESIGN: The overview is the Portal intent hub. MCP Connect complements the
// ratified Factory/Forge entry cards without performing live authorization.
import {
  Cable, Check, CheckCircle, ChevronRight, Code2, DollarSign, Eye,
  MessageSquare, Plus, RefreshCw, X, AlertTriangle,
  Wand2, Loader2, Rocket, Shield,
} from "lucide-react";
import { useBrandedDialog } from '../components/BrandedDialog';
import api from "../api";
import type { ProjectSummary, PendingAction, AggregateFindings } from "../api";
import { useApi } from "../useApi";
import { useAuth } from "../context/AuthContext";
import { TopRisks, buildFixPrompt, buildFixAllPrompt, type RiskPattern } from "../components/TopRisks";
import { ownedRiskProjectIds, queueRiskRepairDraft } from '../lib/risk-repair-draft.js';
import { Dot } from "../components/shared/Dot";
import { eC } from "../lib/helpers";
import { metric, money, type Cost, type Caps } from "./Metrics";

/* ─── Action Panel (Overview) ─────────────────────────────────── */

function ActionPanel({ actions, onApprove, onRetry, onViewDiff, onNavigateChat, onDismiss, onDismissAll }: {
  actions: PendingAction[];
  onApprove: (a: PendingAction) => void;
  onRetry: (a: PendingAction) => void;
  onViewDiff: (a: PendingAction) => void;
  onNavigateChat: (a: PendingAction) => void;
  onDismiss: (a: PendingAction) => void;
  onDismissAll: () => void;
}) {
  const [resolving, setResolving] = useState<Set<string>>(new Set());
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const visibleActions = actions.filter((a: PendingAction) => !dismissed.has(a.id));
  if (visibleActions.length === 0) return null;

  const hasFailedActions = visibleActions.some((a: PendingAction) => a.type === 'sync_failed');

  const handleResolve = (id: string, cb: () => void) => {
    setResolving((prev: Set<string>) => new Set(prev).add(id));
    cb();
    setTimeout(() => setResolving((prev: Set<string>) => { const n = new Set(prev); n.delete(id); return n; }), 400);
  };

  const handleDismiss = (a: PendingAction) => {
    setDismissed((prev: Set<string>) => new Set(prev).add(a.id));
    onDismiss(a);
  };

  const { confirm: brandedConfirm } = useBrandedDialog();
  const handleDismissAll = async () => {
    if (!(await brandedConfirm('Dismiss all failed sync entries?'))) return;
    const failedIds = visibleActions.filter((a: PendingAction) => a.type === 'sync_failed').map((a: PendingAction) => a.id);
    setDismissed((prev: Set<string>) => { const n = new Set(prev); failedIds.forEach((id: string) => n.add(id)); return n; });
    onDismissAll();
  };

  return (
    <div className="action-panel">
      <div className="action-panel-header">
        <div className="action-panel-title">
          <span className="action-dot" />
          Needs your attention
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="action-count">{visibleActions.length} pending</span>
          {hasFailedActions && (
            <button
              className="btn btn-sm"
              style={{ fontSize: 'var(--text-xs)', padding: '2px 8px', opacity: 0.7 }}
              onClick={handleDismissAll}
            >
              Clear All
            </button>
          )}
        </div>
      </div>
      <div className="action-list">
        {visibleActions.map((a: PendingAction) => (
          <div key={a.id} className={`action-item${resolving.has(a.id) ? ' resolving' : ''}`}>
            <div className="action-item-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span className="mono" style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', fontWeight: 500 }}>{a.project_name}</span>
                {a.repo && <span className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{a.repo}</span>}
              </div>
              <span className="action-item-title">{a.title}</span>
              {a.description && <span className="action-item-desc" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)', lineHeight: 1.4 }}>{a.description}</span>}
            </div>
            <div className="action-item-actions">
              {(a.type === 'human_gate' || a.type === 'sync_review') && (
                <>
                  <button className="btn btn-sm btn-primary" onClick={() => handleResolve(a.id, () => onApprove(a))}>
                    <Check size={11} /> {a.type === 'sync_review' ? 'Approve Sync' : 'Approve & Merge'}
                  </button>
                  {(a.pr_number || a.sync_run_id) && (
                    <button className="btn btn-sm" onClick={() => onViewDiff(a)}>
                      <Eye size={11} /> View Diff
                    </button>
                  )}
                </>
              )}
              {a.type === 'waiting_for_input' && (
                <button className="btn btn-sm" onClick={() => onNavigateChat(a)}>
                  <MessageSquare size={11} /> Answer in Chat &rarr;
                </button>
              )}
              {a.type === 'sync_failed' && (() => {
                // 3+ grouped failures: retrying again rarely helps -- lead with Diagnose.
                const attempts = ((a as any)._group_ids as string[] | undefined)?.length ?? 1;
                const stuck = attempts >= 3;
                return (
                  <>
                    {stuck && (
                      <button className="btn btn-sm btn-primary" onClick={() => onNavigateChat(a)}>
                        <Wand2 size={11} /> Diagnose
                      </button>
                    )}
                    <button className={`btn btn-sm${stuck ? '' : ' btn-primary'}`} onClick={() => handleResolve(a.id, () => onRetry(a))}>
                      <RefreshCw size={11} /> {stuck ? 'Retry' : 'Retry sync'}
                    </button>
                    <button className="btn btn-sm" aria-label={`View logs for ${a.project_name}`} title="View logs" onClick={() => onViewDiff(a)}>
                      <Eye size={11} />
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ padding: '2px 6px', opacity: 0.6 }}
                      aria-label={`Dismiss ${a.project_name}`}
                      title="Dismiss"
                      onClick={() => handleDismiss(a)}
                    >
                      <X size={11} />
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Cross-Repo Insights Wrapper ──────────────────────────────── */

function TopRisksWrapper({ go, projects, aggregate, onFixIt }: {
  go: (p: string) => void;
  projects: ProjectSummary[];
  aggregate: AggregateFindings;
  onFixIt?: (prompt: string, projectId?: string) => void;
}) {
  const { subscriber } = useAuth();
  const [selection, setSelection] = React.useState<RiskPattern[] | null>(null);
  const [handoffError, setHandoffError] = React.useState<string | null>(null);
  const projectNames: Record<string, string> = Object.fromEntries(
    projects.map((p: ProjectSummary) => [p.project_id, p.display_name]),
  );
  const candidates = (patterns: RiskPattern[]) => ownedRiskProjectIds(
    patterns.flatMap((pattern: RiskPattern) => pattern.projectIds ?? []),
    projects.map((project: ProjectSummary) => project.project_id),
  );
  const openChat = (patterns: RiskPattern[], projectId: string) => {
    if (!subscriber?.id || !candidates(patterns).includes(projectId)) return;
    const selected = patterns.filter((pattern: RiskPattern) => pattern.projectIds?.includes(projectId));
    const names = { [projectId]: projectNames[projectId] };
    const prompt = selected.length === 1 ? buildFixPrompt(selected[0], names) : buildFixAllPrompt(selected, names);
    if (!queueRiskRepairDraft(subscriber.id, projectId, prompt, undefined, Date.now(), selected.map((pattern: RiskPattern) => ({ title: pattern.findingType, category: pattern.category })))) {
      setHandoffError('Could not open the repair draft. Please try again.');
      return;
    }
    setSelection(null);
    go(`subscriber-chat:project:${projectId}`);
  };
  const choose = (patterns: RiskPattern[]) => {
    setHandoffError(null);
    const ids = candidates(patterns);
    if (ids.length === 1) openChat(patterns, ids[0]);
    else if (ids.length > 1) setSelection(patterns);
    else {
      setSelection(null);
      const prompt = patterns.length === 1
        ? buildFixPrompt(patterns[0], projectNames)
        : buildFixAllPrompt(patterns, projectNames);
      if (onFixIt) onFixIt(prompt);
      else go('subscriber-chat');
    }
  };
  return <>
    <TopRisks aggregate={aggregate} onFix={(pattern: RiskPattern) => choose([pattern])}
      onFixAll={() => choose(aggregate.patterns ?? [])} projectNames={projectNames} />
    {handoffError && <p role="alert">{handoffError}</p>}
    {selection && <section aria-label="Choose repair repository">
      <p>Choose the repository for this repair draft.</p>
      {candidates(selection).map((id: string) => <button key={id} type="button"
        onClick={() => openChat(selection, id)}>{projectNames[id]}</button>)}
      <button type="button" onClick={() => setSelection(null)}>Cancel</button>
    </section>}
  </>;
}

/* ─── Cost KPI Strip (ocn-S657-P0) ─────────────────────────────── */
// Glance layer only — reuses the EXISTING GET /api/subscriber/metrics/cost
// and /caps endpoints (S638) via the shared `metric()` helper exported from
// Metrics.tsx. NEVER duplicates that query logic. Every nested field is
// optional-chained: an empty {}, a partial response, or a rejected fetch
// must render zero/empty state, never throw (this page crashing is strictly
// worse than the Metrics detail page crashing — see S652 white-screen scar).

function UsageCard({ go }: { go: (p: string) => void }) {
  const { data: cost } = useApi<Cost | null>(() => metric<Cost>('cost?scope=me').catch(() => null), []);
  const { data: caps } = useApi<Caps | null>(() => metric<Caps>('caps?scope=me').catch(() => null), []);

  const mtd = cost?.totals?.mtd ?? 0;
  const today = cost?.totals?.today ?? 0;
  const burnRate = cost?.derived?.burnRatePerDay ?? 0;
  const anyCapSet = caps?.anyCapSet ?? false;
  const monthlyCap = caps?.windowedCaps?.[0]?.monthly_usd ?? null;
  const remaining = monthlyCap != null ? Math.max(0, monthlyCap - mtd) : null;

  const toMetrics = () => go('metrics');

  return (
    <section className="ov-card ov-usage" aria-labelledby="ov-usage-h">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 id="ov-usage-h" className="ov-card-title">Usage</h3>
        <span className="ov-usage-label">{anyCapSet ? 'Cap active' : 'Pay as you go'}</span>
      </div>
      <div>
        <div className="ov-usage-label">This month</div>
        <div className="ov-usage-big">{money(mtd)}</div>
      </div>
      <dl className="ov-usage-rows">
        <div className="ov-usage-row"><dt>Today</dt><dd>{money(today)}</dd></div>
        <div className="ov-usage-row"><dt>Avg per day (7d)</dt><dd>{money(burnRate)}</dd></div>
        <div className="ov-usage-row">
          <dt>Spend cap</dt>
          <dd>
            {!anyCapSet
              ? <button type="button" className="ov-link" onClick={toMetrics}>Set a cap</button>
              : (remaining != null ? `${money(remaining)} left of ${money(monthlyCap ?? 0)}` : 'Active')}
          </dd>
        </div>
      </dl>
      <button type="button" className="btn" onClick={toMetrics}>Usage details</button>
    </section>
  );
}

/* ─── Getting Started (empty-state intent hub) ───────────────── */

function GettingStarted({ go }: { go: (p: string) => void }) {
  const cards = [
    {
      icon: <Rocket size={20} />,
      title: 'Build a new app',
      desc: 'Describe your idea in plain English. OCNexus asks a few questions, writes the spec, then builds, tests, and deploys a working SaaS app.',
      cta: 'Start building',
      // ocn-S278-P0: go straight to subscriber-chat in app_wizard mode — no wizard gating screen.
      action: () => go('subscriber-chat:app_wizard'),
      accent: '#3b82f6',
    },
    {
      icon: <Code2 size={20} />,
      title: 'Review & fix a repo',
      desc: 'Connect an existing GitHub repo. OCNexus runs an architecture review, surfaces findings, and opens verified fix PRs.',
      cta: 'Pick a repo',
      // ocn-S278-P0: go to repo picker, then to subscriber-chat in sync_find_fix mode.
      action: () => go('repo-list'),
      accent: '#22c55e',
    },
    {
      icon: <MessageSquare size={20} />,
      title: 'Ask the assistant',
      desc: 'Not sure where to start? Chat with OCNexus about your code, a build, or what to do next.',
      cta: 'Open chat',
      action: () => go('subscriber-chat'),
      accent: '#a855f7',
    },
    {
      icon: <Cable size={20} />,
      title: 'Connect OCNexus MCP',
      desc: 'Review how a compatible AI tool can read safe work and usage context for this workspace.',
      cta: 'Review MCP access',
      action: () => go('mcp-connect'),
      accent: '#00cfe8',
    },
  ];
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ textAlign: 'center', padding: '8px 0 18px' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--text)', fontWeight: 600 }}>Welcome to OCNexus</h3>
        <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>What would you like to do?</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
        {cards.map((card) => (
          <div
            key={card.title}
            className="card"
            style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10, padding: 18 }}
            onClick={card.action}
          >
            <div style={{ width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${card.accent}1a`, color: card.accent }}>
              {card.icon}
            </div>
            <div style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--text)' }}>{card.title}</div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)', lineHeight: 1.5, flex: 1 }}>{card.desc}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: card.accent, fontSize: 'var(--text-sm)', fontWeight: 500 }}>
              {card.cta} <ChevronRight size={13} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Overview Page ──────────────────────────────────────────── */

export function OverviewPage({ go, projects, onFixIt }: { go: (p: string) => void; projects: ProjectSummary[]; onFixIt?: (prompt: string, projectId?: string) => void }) {
  // Pending actions — poll every 15s
  const { sessionExpired, subscriber } = useAuth();
  const { data: aggregate } = useApi<AggregateFindings | null>(
    () => subscriber?.id ? api.findings.aggregate(subscriber.id) : Promise.resolve(null),
    [subscriber?.id],
  );
  const { data: actions, refetch: refetchActions } = useApi(() => api.getPendingActions(), []);
  useEffect(() => {
    if (sessionExpired) return; // Stop polling on 401
    const iv = setInterval(refetchActions, 15_000);
    const onVis = () => { if (document.visibilityState === 'visible') refetchActions(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [refetchActions, sessionExpired]);

  // Navigate to the Sync page scoped to THIS project (not projects[0]).
  const goToSync = (a: PendingAction) => go(a.project_id ? `sync:${a.project_id}` : 'sync');

  const handleApprove = async (a: PendingAction) => {
    if (a.type === 'human_gate' && a.run_id) {
      await api.chatApprove(a.run_id, 'approve').catch(() => {});
    } else if (a.type === 'sync_review' && a.sync_run_id) {
      await api.syncApprove(a.sync_run_id, true).catch(() => {});
      // Approving a sync resumes the scan (post-review → arch gate) — take the
      // user to the live Sync UX instead of leaving them on Overview.
      refetchActions();
      goToSync(a);
      return;
    }
    refetchActions();
  };

  const handleRetry = async (a: PendingAction) => {
    if (a.type === 'sync_failed') {
      await api.syncRun(a.project_id, 'retry').catch(() => {});
      // Navigate to Sync page so user can watch progress
      goToSync(a);
    }
    refetchActions();
  };

  const handleViewDiff = async (a: PendingAction) => {
    if (a.sync_run_id) {
      // Navigate to Sync page to view the review diff
      goToSync(a);
    } else if (a.pr_number) {
      // Fallback to GitHub PR diff
      window.open(`https://github.com/${a.project_name}/pull/${a.pr_number}/files`, '_blank');
    }
  };

  const handleNavigateChat = (a: PendingAction) => {
    // Open that project's own chat (fix checklist), not the generic workspace chat.
    if (a.project_id) {
      void api.createSubscriberChat(a.project_id)
        .then((r: { chatSessionId: string }) => go(`subscriber-chat:chat:${r.chatSessionId}`))
        .catch(() => {
          sessionStorage.setItem('chat_target_project', a.project_id);
          if (a.run_id) sessionStorage.setItem('chat_target_run', a.run_id);
          go(`subscriber-chat:project:${a.project_id}`);
        });
      return;
    }
    go('subscriber-chat');
  };

  const handleDismiss = async (a: PendingAction) => {
    const runId = a.sync_run_id || a.id.replace('sync_', '');
    // Dismiss all grouped runs if deduped
    const groupIds = (a as any)._group_ids as string[] | undefined;
    if (groupIds && groupIds.length > 1) {
      await Promise.all(groupIds.map((id: string) => api.dismissAction(id).catch(() => {})));
    } else {
      await api.dismissAction(runId).catch(() => {});
    }
    refetchActions();
  };

  const handleDismissAll = async () => {
    await api.dismissAllActions().catch(() => {});
    refetchActions();
  };

  const visibleProjects = projects.filter((p: ProjectSummary) => p.project_id !== "example-app");
  const failedActions = ((actions as PendingAction[] | null | undefined) ?? [])
    .filter((a: PendingAction) => a.type === 'sync_failed');
  const failedProjectIds = new Set(failedActions.map((a: PendingAction) => a.project_id));
  const failedCount = failedActions.reduce(
    (count: number, action: PendingAction) => count +
      (((action as PendingAction & { _group_ids?: string[] })._group_ids?.length) ?? 1),
    0,
  );

  return (
    <div>
      <div className="ov-head">
        <div>
          <h2 className="ov-title">Overview</h2>
          <p className="ov-sub">
            {visibleProjects.length} project{visibleProjects.length === 1 ? '' : 's'} · {aggregate?.totalOpenFindings ?? 0} open findings
            {failedCount > 0 && (
              <span className="ov-sub-alert"> · {failedCount} sync{failedCount === 1 ? ' needs' : 's need'} attention</span>
            )}
          </p>
        </div>
        <div className="ov-head-actions">
          <button className="btn" onClick={() => go("subscriber-chat")}><MessageSquare size={12} /> Ask OCNexus</button>
          <button className="btn btn-primary" onClick={() => go("new-project")}><Plus size={12} /> New project</button>
        </div>
      </div>

      {/* Empty state — intent hub for new subscribers with no projects yet */}
      {projects.length === 0 && !sessionExpired && <GettingStarted go={go} />}

      {/* Action Required panel — only renders when there are pending actions */}
      {actions && actions.length > 0 && (
        <ActionPanel
          actions={actions}
          onApprove={handleApprove}
          onRetry={handleRetry}
          onViewDiff={handleViewDiff}
          onNavigateChat={handleNavigateChat}
          onDismiss={handleDismiss}
          onDismissAll={handleDismissAll}
        />
      )}

      {/* Risks + usage (Overview redesign) -- glance layer, links to Metrics for depth */}
      {!sessionExpired && projects.length > 0 && (
        <div className="ov-grid">
          {aggregate && aggregate.totalProjects >= 1 && (
            <TopRisksWrapper go={go} projects={visibleProjects} aggregate={aggregate} onFixIt={onFixIt} />
          )}
          <UsageCard go={go} />
        </div>
      )}

      {visibleProjects.length > 0 && <h3 id="overview-projects" className="ov-section-title">Projects</h3>}
      <div className="ov-proj-grid">
        {visibleProjects.map((p: ProjectSummary) => {
          const ws = p.wizard_state as import('../api').WizardStateFromDB | null | undefined;
          const completedServices = ws?.steps ? Object.entries(ws.steps).filter(([, v]) => v === 'complete').map(([k]) => k) : [];
          const capabilities = ws?.unlockedCapabilities ?? [];
          return (
            <div key={p.project_id} className="card" style={{ cursor: "pointer" }} onClick={() => go(`project:${p.project_id}`)}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <Dot status={failedProjectIds.has(p.project_id) ? "failed" : "healthy"} />
                    <span style={{ fontSize: "var(--text-base)", fontWeight: 500, color: "var(--text)" }}>{p.display_name}</span>
                    {p.wizard_complete && <span className="badge" style={{ background: 'var(--green)15', color: 'var(--green)', fontSize: 9 }}>✓ Wizard</span>}
                    {failedProjectIds.has(p.project_id) && <span className="ov-pill-failed">Sync stopped</span>}
                  </div>
                  <div className="mono muted" style={{ fontSize: "var(--text-xs)", marginTop: 3 }}>{p.repo}</div>
                </div>
                <ChevronRight size={12} style={{ color: "var(--border)" }} />
              </div>
              <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
                <span className="badge badge-library">{p.packages} pkg{p.packages !== 1 ? "s" : ""}</span>
                {completedServices.map((svc: string) => (
                  <span key={svc} className="badge" style={{ background: 'var(--green)10', color: 'var(--green)', fontSize: 10 }}>{svc}</span>
                ))}
              </div>
              {capabilities.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 'var(--text-xs)', color: 'var(--text-dim)', lineHeight: 1.5 }}>
                  {capabilities.slice(0, 3).map((c: string, i: number) => <div key={i}>{c}</div>)}
                  {capabilities.length > 3 && <div style={{ color: 'var(--text-muted)' }}>+{capabilities.length - 3} more</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
